<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="toggleDialog"
    max-width="600"
  >
    <v-card>
      <v-row class="fluid justify-space-between align-top ma-2">
        <v-card-title class="d-flex align-center">
          <v-icon size="26" color="warning" class="mr-3">mdi-alert-circle-outline</v-icon>
          <span class="text-h5"><span class="rounded-lg px-1">Generation Failures</span></span>
        </v-card-title>
        <v-btn variant="text" icon color="grey" @click="toggleDialog(false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-row>

      <v-card-text>
        <p class="mb-4">
          The AI service encountered a temporary issue, which can happen when the model is busy.
          You can try again, or select a different AI model before retrying the failed steps.
        </p>
        <v-list density="compact" lines="one">
          <v-list-item
            v-for="(item, index) in failedItems"
            :key="index"
            class="pa-0"
          >
            <template #prepend>
              <v-icon
                size="small"
                :color="colors[item.type]"
              >
                {{ icons[item.type] || 'mdi-file-code-outline' }}
              </v-icon>
            </template>
            <v-list-item-title>
              <span class="text-capitalize font-weight-bold">{{ item.type.replace('-', ' ') }}: </span>
              <span>{{ getItemName(item) }}</span>
            </v-list-item-title>
          </v-list-item>
        </v-list>
        <div class="w-50 mt-6">
          <ai-version-selector v-model="selectedAiModel"/>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="outlined" size="large" @click="toggleDialog(false)">Close</v-btn>
        <v-btn
          id="generate-workflow-from-code-btn"
          variant="flat"
          color="primary"
          size="large"
          @click="handleRetry"
        >
          <v-icon start>mdi-sync</v-icon>
          Retry Failed Items
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import AiVersionSelector from '@/components/AiVersionSelector.vue'

defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  failedItems: {
    type: Array,
    required: true
  }
})

const emit = defineEmits(['update:modelValue', 'retry'])

const store = useStore()
const workflow = computed(() => store.state.workflow.workflow)
const selectedAiModel = ref(workflow.value.aiVersion || 11)

const icons = {
  command: 'mdi-flash',
  readmodel: 'mdi-magnify',
  entity: 'mdi-shape-outline',
  gwt: 'mdi-timeline',
  frontend: 'mdi-react',
  diagram: 'mdi-sitemap',
  cards: 'mdi-card-multiple-outline',
  commands: 'mdi-database-edit-outline',
  queries: 'mdi-database-search-outline',
  entities: 'mdi-database-outline'
}
const colors = {
  command: 'blue-lighten-1',
  readmodel: 'green-lighten-1',
  entity: 'amber-darken-1',
  gwt: 'purple-lighten-1',
  frontend: 'blue-lighten-2',
  diagram: 'red-lighten-1',
  cards: 'teal-lighten-1',
  commands: 'indigo-lighten-1',
  queries: 'cyan-lighten-1',
  entities: 'orange-darken-1'
}

const getItemName = (item) => {
  if (item.name) {
    return item.name
  }

  const target = item.diff.new || item.diff.old

  return target.name || target.description
}

const toggleDialog = (value) => {
  emit('update:modelValue', value)
}

const handleRetry = () => {
  emit('retry')
  emit('update:modelValue', false)
}

watch(() => workflow.value.aiVersion, (newVal) => {
  selectedAiModel.value = newVal || 11
})
</script>
